import React, {useEffect, useRef, useState} from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import styles from './FreePoolAggregatesTable.module.css';
import {AutoSizer, List} from 'react-virtualized';
import {Popover, LinearProgress} from '@material-ui/core';
import {columnsPropType} from '../../types';
import {TableRow, TableHead} from '../../atomicComponents';

const FreePoolAggregatesTable = ({
  rows: data,
  columns: originalColumns,
  classNames = {},
  rowHeight = 38,
  headerHeight,
  defaultColumns: originalDefaultColumns,
  isDataLoading,
}) => {
  const [rowsData, setRowsData] = useState({
    data: [...data],
    filteredData: [...data],
  });
  const [selectedColumns, setSelectedColumns] = useState(
    originalDefaultColumns,
  );
  const noOfFixedColumns = originalDefaultColumns.length;

  const [showCopyAlert, setShowCopyAlert] = useState(false);
  const [fieldToSearchTextMap, setFieldToSearchTextMap] = useState({});
  const [isEllipsisPopupOpen, setIsEllipsisPopupOpen] = useState(false);
  const [ellipsisAnchorEl, setEllipsisAnchorEl] = useState(null);
  const [ellipsisPopupValue, setEllipsisPopupValue] = useState(null);
  const [fieldToFiltersSetMap, setFieldToFiltersSetMap] = useState({});

  const closeEllipsisPopup = () => setIsEllipsisPopupOpen(false);

  const vListRef = useRef(null);
  const tableHeadRef = useRef(null);

  const handleListScroll = e => {
    if (e.target.scrollLeft)
      tableHeadRef.current.scrollLeft = e.target.scrollLeft;
  };

  

  const handlePopoverOpen = (event, val) => {
    if (event.currentTarget.scrollWidth > event.currentTarget.clientWidth) {
      setEllipsisPopupValue(val);
      setIsEllipsisPopupOpen(true);
      setEllipsisAnchorEl(event.currentTarget);
    }
  };

  const handlePopoverClose = () => {
    setIsEllipsisPopupOpen(false);
    setEllipsisAnchorEl(null);
  };



  const reorder = (list, startIndex, endIndex) => {
    const result = [...list];
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result;
  };

  const reorderHeaders = result => {
    // dropped outside the list or dropped at serial number place
    if (!result.destination || result.destination.index < noOfFixedColumns) {
      return;
    }

    const reorderedColumns = reorder(
      selectedColumns,
      result.source.index,
      result.destination.index,
    );

    setSelectedColumns(reorderedColumns);
  };

  const rowRenderer = ({style, index, key}) => {
    const handleCopy = text => {
      navigator.clipboard.writeText(text);
      setShowCopyAlert(true);
    };

    return (
      <TableRow
        index={index}
        style={style}
        key={key}
        row={rowsData.filteredData[index]}
        columns={selectedColumns}
        classNames={{...classNames}}
        rowHeight={rowHeight}
        onCopy={handleCopy}
        handlePopoverOpen={handlePopoverOpen}
        handlePopoverClose={handlePopoverClose}
      />
    );
  };

  useEffect(() => {
    if (showCopyAlert) setShowCopyAlert(false);
  }, [showCopyAlert]);

  useEffect(() => {
    setRowsData(prevState => {
      const dataFilteredBySearchTexts = filterBySearchTexts(
        fieldToSearchTextMap,
        prevState.data,
        selectedColumns,
      );

      const dataFilteredBySearchAndFilters = filterBySelectedFilters(
        fieldToFiltersSetMap,
        dataFilteredBySearchTexts,
        selectedColumns,
      );

      return {...prevState, filteredData: dataFilteredBySearchAndFilters};
    });
  }, [
    selectedColumns,
    fieldToFiltersSetMap,
    fieldToSearchTextMap,
    rowsData.data,
  ]);
  useEffect(() => {
    vListRef.current = document.getElementsByClassName(
      'ReactVirtualized__Grid ReactVirtualized__List',
    )[0];

    if (vListRef.current)
      vListRef.current.addEventListener('scroll', handleListScroll);

    return () =>
      vListRef.current.removeEventListener('scroll', handleListScroll);
  }, []);
  useEffect(() => {
    setRowsData({
      data: [...data],
      filteredData: [...data],
    });
  }, [data]);

  return (
    <>
      <div
        style={{width: '100%', height: '100%'}}
        className={clsx(
          styles.baseContainer,
          classNames.tableContainerClassName,
        )}
      >
        <div
          style={{width: '100%'}}
          className={clsx(
            styles.basicTableContainer,
            classNames.tableHeadCellClassName,
          )}
        >
          <AutoSizer>
            {({width: windowWidth, height: windowHeight}) => {
              let width = windowWidth;
              let height = windowHeight;
              const listHeight = height - rowHeight - 2;
              return (
                <div
                  className={clsx(styles.basicTable, classNames.tableClassName)}
                  style={{
                    height,
                    width,
                  }}
                >
                  <TableHead
                    columns={selectedColumns}
                    ref={tableHeadRef}
                    classNames={classNames}
                    height={headerHeight || rowHeight}
                    reorderHeaders={reorderHeaders}
                    rows={rowsData.filteredData}
                    allRows={rowsData.data}
                    fieldToSearchTextMap={fieldToSearchTextMap}
                    setSearchTextForTitleCurry={field => value =>
                      setFieldToSearchTextMap(prev => ({
                        ...prev,
                        [field]: value.toLowerCase(),
                      }))}
                    noOfFixedColumns={noOfFixedColumns}
                    fieldToFiltersSetMap={fieldToFiltersSetMap}
                    setFiltersForFieldCurry={field => selectedFilters =>
                      setFieldToFiltersSetMap(prev => ({
                        ...prev,
                        [field]: new Set(selectedFilters),
                      }))}
                  />
                  <List
                    ref={vListRef}
                    containerStyle={{overflow: 'initial'}}
                    rowRenderer={rowRenderer}
                    height={listHeight}
                    rowHeight={rowHeight}
                    rowCount={rowsData.filteredData.length}
                    width={width}
                    className={clsx(
                      classNames.tableHeadCellClassName,
                      classNames.tableContainerClassName,
                      styles.scrollBar,
                      styles.heightAdjustedListScroll,
                    )}
                    overscanRowCount={10}
                  />
                  {isDataLoading && (
                    <div>
                      <LinearProgress />
                    </div>
                  )}
                </div>
              );
            }}
          </AutoSizer>
        </div>
      </div>

      <Popover
        open={isEllipsisPopupOpen}
        anchorEl={ellipsisAnchorEl}
        onClose={closeEllipsisPopup}
        className={styles.popover}
        classes={{
          paper: styles.popoverPaper,
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        {ellipsisPopupValue}
      </Popover>
      <div
        className={clsx(
          styles.copyAlert,
          showCopyAlert ? styles.visible : styles.hidden,
        )}
      >
        Text copied
      </div>
    </>
  );
};

FreePoolAggregatesTable.propTypes = {
  classNames: PropTypes.shape({
    tableContainerClassName: PropTypes.string,
    tableClassName: PropTypes.string,
    tableRowClassName: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    tableHeadClassName: PropTypes.string,
  }),
  rows: PropTypes.array.isRequired,
  columns: columnsPropType,
  rowHeight: PropTypes.number,
  headerHeight: PropTypes.number,
  defaultColumns: columnsPropType,
};

export default FreePoolAggregatesTable;

function filterBySearchTexts(fieldToSearchTextMap, data, columns) {
  const fieldToSearchFnMap = columns.reduce((acc, col) => {
    if (col.searchFn) return {...acc, [col.field]: col.searchFn};
    return acc;
  }, {});

  let filteredData = [...data];
  Object.entries(fieldToSearchTextMap).forEach(entry => {
    const [field, searchText] = entry;
    const defaultSearchFn = (fieldValue, searchValue) =>
      fieldValue.toLowerCase().startsWith(searchValue);

    const searchFn = fieldToSearchFnMap[field] || defaultSearchFn;

    filteredData = filteredData.filter(
      row => !searchText || !row[field] || searchFn(row[field], searchText),
    );
  });
  return filteredData;
}

function filterBySelectedFilters(fieldToFiltersSetMap, data, columns) {
  const fieldToFilterFnMap = columns.reduce((acc, col) => {
    if (col.filterFn) return {...acc, [col.field]: col.filterFn};
    return acc;
  }, {});

  let filteredData = [...data];
  Object.entries(fieldToFiltersSetMap).forEach((entry, idx) => {
    const [field, filtersSet] = entry;
    const filterFn = fieldToFilterFnMap[field];

    filteredData = filteredData.filter(row => {
      if (!filterFn) return !filtersSet?.size || filtersSet.has(row[field]);
      if (!idx) {
        console.log(' [...filtersSet]', [...filtersSet]);
      }

      return (
        !filtersSet?.size ||
        [...filtersSet].some(filter => filterFn(row, filter.value))
      );
    });
  });
  return filteredData;
}
